export const namespaced = true

import Vue from "vue"
import router from "@/router/index.js";
import { DateTime } from "luxon"
const moment = require("moment")

// Import the centralized Google Maps service
import GoogleMapsService from "@/services/GoogleMapsService.js"

// Helper function to handle date serialization
const serializeDates = (dates) => {
  if (Array.isArray(dates)) {
    return JSON.stringify(
      dates.map((date) => (date instanceof Date ? date.toISOString() : null))
    )
  }
  return "[]"
}

const deserializeDates = (dateString) => {
  try {
    return JSON.parse(dateString).map((date) => (date ? new Date(date) : null))
  } catch {
    return []
  }
}

export const state = () => ({
  searchDates: deserializeDates(sessionStorage.getItem("searchDates") || "[]"),
  searchLocation: JSON.parse(sessionStorage.getItem("searchLocation") || '{ "name": "", "latitude": 0, "longitude": 0}'),
  advancedSearch: JSON.parse(
    sessionStorage.getItem("advancedSearch") ||
      '{"active": false, "chains": [], "radius": 5, "starRating": "No Preference"}'
  ),
  amenitiesCount: {},
  availableChains: [],
  searchErrors: [],
  searchResults: [],
  searchMeta: {},
  showRefreshNotification: false,
  showResults: false,
  resultsLoading: false,
  recentSearches: JSON.parse(localStorage.getItem("recentSearches") || "[]"),
  savedResults: JSON.parse(localStorage.getItem("savedResults") || "[]"),
  lastViewedHotel: localStorage.getItem("lastViewedHotel") || "",
  minPrice: 0,
  maxPrice: 0,
  filters: {
    companyPreferred: {
      id: "companyPreferred",
      name: "Company Preferred Hotels",
      selected: "all", // default
      options: [
        { value: "all", label: "All" },
        { value: "preferred", label: "Preferred only" },
      ],
    },
    
    price: {
      id: "price",
      name: "Price",
      range: [0, 0],
      selected: [],
    },
    policy: {
      id: "policy",
      name: "Policy",
      selected: "any",
      options: [
        { value: "hard", label: "In Policy Only" },
        { value: "soft", label: "Allow Manager Notifications" },
        { value: "any", label: "Any" },
      ],
    },
    chains: {
      id: "chains",
      name: "Hotel Chains",
      selected: [],
      options: [],
      highlightedOptions: [], //preferred chains
    },
    brands: {
      id: "brands",
      name: "Hotel Brands",
      selected: [],
      options: [
        { value: "EC", label: "Choice Brands" },
        { value: "EH", label: "Hilton Hotels" },
        { value: "6C", label: "Intercontinental Hotels Group" },
        { value: "EM", label: "Marriott Brands" },
        { value: "WY", label: "Wyndham Hotels" },
      ],
    },
    rating: {
      id: "rating",
      name: "Rating",
      selected: [],
      options: [
        { value: "1", label: "1 Star" },
        { value: "2", label: "2 Star" },
        { value: "3", label: "3 Star" },
        { value: "4", label: "4 Star" },
        { value: "5", label: "5 Star" },
      ],
    },
    amenities: {
      id: "amenities",
      name: "Amenities",
      selected: [],
      options: [
        {
          value: "Complimentary high speed internet in room",
          label: "Free Internet Access",
          icon: "fa-solid fa-globe",
        },
        { value: "Safe", label: "In-room safe", icon: "fa-solid fa-vault" },
        {
          value: "Air conditioning",
          label: "Air conditioning",
          icon: "fa-solid fa-fan",
        },
        {
          value: "Wireless internet connection",
          label: "Wi-fi available",
          icon: "fa-solid fa-wifi",
        },
        {
          value: "Pets allowed",
          label: "Pets allowed",
          icon: "fa-solid fa-paw",
        },
      ],
    },
  },
  roomFilters: {
    guarantee: {
      id: "guarantee",
      name: "Guarantee",
      selected: [],
      options: [
        { value: "PrePay", label: "In-room safe", icon: "fa-solid fa-vault" },
        {
          value: "Deposit",
          label: "Air conditioning",
          icon: "fa-solid fa-fan",
        },
        { value: null, label: "Any", icon: "fa-solid fa-wifi" },
      ],
    },
    beds: {
      id: "beds",
      name: "Beds",
      selected: [],
      options: [
        { value: 1, label: "1 bed", icon: "fa-solid fa-vault" },
        { value: 2, label: "2 beds", icon: "fa-solid fa-fan" },
        { value: 3, label: "3 or more", icon: "fa-solid fa-wifi" },
      ],
    },
    freeBreakfast: {
      id: "breakfast",
      name: "Free Breakfast",
      selected: [],
      options: [
        {
          value: true,
          label: "Free Breakfast Included",
          icon: "fa-solid fa-utensils",
        },
        { value: false, label: "Any", icon: "fa-solid fa-fan" },
      ],
    },
    refundable: {
      id: "refundable",
      name: "Fully Refundable",
      selected: [],
      options: [
        {
          value: true,
          label: "Free Cancellation",
          icon: "fa-solid fa-utensils",
        },
        { value: false, label: "Any", icon: "fa-solid fa-fan" },
      ],
    },
  },
  sortOrder: "Recommended",
})

export const mutations = {
  RESET_STATE(state) {
    state.searchDates = []
    state.searchLocation = {
      name: "",
      latitude: 0,
      longitude: 0,
    }
    state.advancedSearch = {"active": false, "chains": [], "radius": 5, "starRating": "No Preference"}
    state.amenitiesCount = {}
    state.availableChains = []
    state.searchErrors = []
    state.searchResults = []
    state.searchMeta = {}
    state.showRefreshNotification = false
    state.showResults = false
    state.resultsLoading = false
    state.recentSearches = []
    state.savedResults = []
    state.lastViewedHotel = ""   
    state.minPrice = 0
    state.maxPrice = 0

    // Reset all filters
    Object.keys(state.filters).forEach((key) => {
      state.filters[key].selected = []
    })
    Object.keys(state.roomFilters).forEach((key) => {
      state.roomFilters[key].selected = []
    })
    state.sortOrder = "Recommended"

    // Update sessionStorage
    sessionStorage.setItem("searchDates", "[]")
    sessionStorage.setItem("advancedSearch", '{"active": false, "chains": [], "radius": 5, "starRating": "No Preference"}')

    localStorage.setItem("recentSearches" , "[]")
    localStorage.setItem("savedResults", "[]")
    localStorage.setItem("lastViewedHotel", "")


  },

  CLEAR_STATE(state) {
    console.log("CLEARING_HOTEL_STATE")
    this.commit("hotelSearch/RESET_STATE")
    
    // Clear sessionStorage
    sessionStorage.removeItem("searchDates")
    sessionStorage.removeItem("advancedSearch")
    sessionStorage.removeItem("searchLocation")

    // Clear localStorage
    localStorage.removeItem("recentSearches")
    localStorage.removeItem("savedResults")
    localStorage.removeItem("lastViewedHotel")


  },

  RESET_FILTERS(state) {
    // Reset price filter

    state.filters.price = {
      ...state.filters.price,
      range: [0, 0],
      selected: [],
    }

    // Reset all other filters' selected arrays while preserving their structure
    Object.keys(state.filters).forEach((filterKey) => {
      if (filterKey !== "price") {
        state.filters[filterKey] = {
          ...state.filters[filterKey],
          selected: [],
        }
      }
    })
  },

  SET_SEARCH_DATES(state, dates) {
    state.searchDates = dates
    sessionStorage.setItem("searchDates", dates)
  },

  SET_SEARCH_LOCATION(state, location) {
    state.searchLocation = location
  },

  SET_REFRESH_NOTIFICATION(state, boolean) {
    state.showRefreshNotification = boolean
  },

  SET_SHOW_RESULTS(state, boolean) {
    state.showResults = boolean
  },

  SET_SEARCH_RESULTS(state, results) {
    state.searchResults = results
  },

  SET_SEARCH_ERRORS(state, errors) {
    state.searchErrors = errors
  },

  SET_SEARCH_META(state, meta) {
    state.searchMeta = meta
  },

  SET_SAVED_RESULTS(state, results) {
    state.savedResults = results
  },

  SET_RESULTS_LOADING(state, boolean) {
    state.resultsLoading = boolean
  },

  SET_AMENITIES_COUNT(state, amenitiesCount) {
    state.amenitiesCount = amenitiesCount
  },

  SET_AVAILABLE_CHAINS(state, chains) {
    state.availableChains = chains
  },

  SET_CHAIN_OPTIONS(state, list) {
    state.filters.chains.options = list
  },

  SET_BRAND_OPTIONS(state, list) {
    state.filters.brands.options = list
  },

  SET_PREFERRED_CHAINS(state, list) {
    state.filters.chains.highlightedOptions = list
  },

  SET_ADVANCED_ACTIVE(state, boolean) {
    state.advancedSearch.active = boolean
  },

  SET_RADIUS(state, radius) {
    state.advancedSearch.radius = radius
  },

  SET_STAR_RATING(state, rating) {
    state.advancedSearch.starRating = rating
  },

  SET_ADVANCED_CHAINS(state, chains) {
    state.advancedSearch.chains = chains
  },

  DELETE_HOTEL(state, hotelCode) {
    state.savedResults = state.savedResults.filter(
      (x) => x.hotelCode !== hotelCode
    )
  },

  SET_LAST_VIEWED_HOTEL(state, hotelCode) {
    state.lastViewedHotel = hotelCode
  },

  SAVE_HOTEL(state, result) {
    const index = state.savedResults.findIndex(
      (i) => i.hotelCode === result.hotelCode
    )
    if (index !== -1) {
      // Update existing hotel
      state.savedResults.splice(index, 1, {
        ...state.savedResults[index],
        ...result,
      })
    } else {
      // Add new hotel
      state.savedResults.push(result)
    }
  },

  //save formData to recent searches array
  SAVE_RECENT_SEARCH(state, payload) {
    const { key, expiresAt, formData } = payload
    let obj = {}
    obj.key = key
    obj.expiresAt = expiresAt
    obj.formData = formData
    state.recentSearches.push(obj)
  },

  // Mutation for setting the selected sort option (i.e. selected dropdown)
  SET_SORT_ORDER(state, sortOrder) {
    state.sortOrder = sortOrder
  },

  SORT_RESULTS(state, sortOrder) {
    console.log("SORT_RESULTS", sortOrder)
    const results = [...state.searchResults] // Create a copy to maintain immutability

    switch (sortOrder) {
      case "Recommended":
  results.sort((a, b) => {
    // Sort by isPreferred (true first), then proximity
    if (a.isPreferred !== b.isPreferred) {
      return b.isPreferred - a.isPreferred
    }
    return a.coordinates.distanceFromSearchedLocation - b.coordinates.distanceFromSearchedLocation
  })
  break

      case "Price Asc":
        results.sort((a, b) => a.cheapestPrice - b.cheapestPrice)
        break
      case "Preferred Vendors":
        results.sort((a, b) => {
          const preferredVendors = state.preferredVendors || []
          return preferredVendors.includes(a.chainCode)
            ? -1
            : preferredVendors.includes(b.chainCode)
            ? 1
            : 0
        })
        break
      case "Price Desc":
        results.sort((a, b) => b.cheapestPrice - a.cheapestPrice)
        break
      case "Alphabetical":
        results.sort((a, b) => a.hotelName.localeCompare(b.hotelName))
        break
      case "Proximity":
        results.sort(
          (a, b) =>
            a.coordinates.distanceFromSearchedLocation - b.coordinates.distanceFromSearchedLocation
        )
        break
      default:
        results.sort(
          (a, b) =>
            a.coordinates.distanceFromSearchedLocation - b.coordinates.distanceFromSearchedLocation
        )
    }

    state.searchResults = results
  },

  // Mutation for setting price range
  SET_PRICE_RANGE(state, [min, max]) {
    state.minPrice = min
    state.maxPrice = max
    // Also update the price filter range to match
    state.filters.price.range = [min, max]
  },

  // Set the filter range only w/o reseting the min/max values
  SET_PRICE_FILTER_RANGE(state, [min, max]) {
    state.filters.price.range = [min, max]
  },
}

export const actions = {
  reset({ commit }) {
    commit("RESET_STATE")
  },

  //Takes the current state of the autocomplete and searchDates objects
  //and format into a flat object sent to the search request
  parseFormData({ state }) {
    const formData = {
      loc: state.searchLocation,
      checkinDate: state.searchDates[0].toISOString().split('T')[0],
      checkoutDate: state.searchDates[1].toISOString().split('T')[0],
      advancedSearch: state.advancedSearch,
    }

    return JSON.parse(JSON.stringify(formData))

  },

  async getData({ commit, state, getters, dispatch }, { cacheKey }) {
    // Reset error and results state before each new search request
    commit("SET_SEARCH_ERRORS", [])
    commit("SET_SEARCH_RESULTS", [])
    commit("RESET_FILTERS") //reset filters after each new search request
    commit("SET_SHOW_RESULTS", true) //show ResultsList.vue component
    commit("SET_RESULTS_LOADING", true)
    commit("SET_REFRESH_NOTIFICATION", false)

    let formData = {}
    let body = {}
    const endpoint = cacheKey
      ? "amadeus/hotel/get-cache"
      : "amadeus/hotel/search"

    if (cacheKey) {
      body = {
        cacheKey: cacheKey,
      }
    } else {

      formData = await dispatch("parseFormData")
      console.log("formData", formData)

      let lat = 0
      let lng = 0
  
      if (formData.loc?.geometry) {
        lat = formData.loc.geometry.location.lat
        lng = formData.loc.geometry.location.lng
      }
      if (formData.loc?.geocode) {
        lat = formData.loc.geocode.latitude
        lng = formData.loc.geocode.longitude
      }
      if (formData.loc?.latitude) {
        lat = formData.loc.latitude
        lng = formData.loc.longitude
      }
  
      lat = parseFloat(lat).toFixed(5)
      lng = parseFloat(lng).toFixed(5)
  
      body = {
        stateful: true,
        sessionKey: null,
        travelerContactId: getters.profile.id,
        normalizeResponse: true,
        requestedCurrency: "USD",
        cacheResponse: true,
        cacheKey: cacheKey,
        infoToReturn: ["generalInfo", "guestRooms", "multimedia"],
        bestRateOnly: true,
        checkInDate: formData.checkinDate,
        checkOutDate: formData.checkoutDate,
        roomCount: 1,
        guestCount: 1,
        location: {
          name: formData.loc.name,
          coordinates: {
            latitude: Number(lat),
            longitude: Number(lng),
          },
          radius: {
            distance: 10,
            distanceUnit: "miles",
          },
        },
      }
  
      if (getters.tripType === "business") {
        body.travelPolicyId = getters.profile.travelPolicyId
      }
  
      if (formData?.advancedSearch?.active === true) {
        body.location.radius.distance = formData.advancedSearch.radius
        if (formData.advancedSearch.starRating !== "No Preference") {
          body.rating = {
            provider: "LSR",
            value: formData.advancedSearch.starRating,
          }
        }
        if (formData.advancedSearch.chains) {
          body.chainCodes = []
  
          for (const [key, value] of formData.advancedSearch.chains) {
            body.chainCodes.push(key)
          }
        }
      }
    }

    let result = await Vue.app.huddleLabsServerCall(endpoint, body)

    if (result.data && result.data.length > 0) {
      //add search results to Vuex state
      commit("SET_SEARCH_RESULTS", result.data)
      commit("SET_SEARCH_META", result.meta)
      commit("SORT_RESULTS", state.sortOrder)
      dispatch('calculateAmenities')
      dispatch('calculateChains')

      if (result.meta?.priceRange) {
        commit("SET_PRICE_RANGE", [
          result.meta.priceRange.min,
          result.meta.priceRange.max,
        ])
      }

      //get cache key and save search to recent searches
      if (result.meta.cacheKey && !result.meta.isCachedResult) {
        //save search form data and search key to Vuex
        commit("SAVE_RECENT_SEARCH", {
          expiresAt: moment()
            .add(600, "seconds") //10 minutes
            .format("YYYY-MM-DD HH:mm:ss"),
          key: result.meta.cacheKey,
          formData: formData,
        })

        //append search key to url and add to browser history
        router.push({
          path: "hotels",
          query: { key: result.meta.cacheKey },
        })
        
      }
    } else if (result.errors) {
      // Handle error cases with proper mutations
      const errors = Object.entries(result.errors)
      commit("SET_SEARCH_ERRORS", [errors])
    } else if (result.message) {
      const errors = [{ title: result.message }]
      commit("SET_SEARCH_ERRORS", errors)
    } else if (result.meta.cacheExpired) {
      commit("SET_REFRESH_NOTIFICATION", true)
    } else {
      const errors = [{ title: "Search failed or no results found." }]
      commit("SET_SEARCH_ERRORS", errors)
    }

    commit("SET_RESULTS_LOADING", false)
    return result
  },

  //use the autoComplete object to set the lat/long of the selected location
  async setLocationFromAutocomplete({ commit }, autocomplete) {

    const location = autocomplete.geometry.location
    const locationName = autocomplete.formatted_address

    // Handle Google Places location functions
    let lng, lat
    if (typeof location.lat === "function") {
      lat = location.lat()
    } else {
      lat = location.lat
    }
    if (typeof location.lng === "function") {
      lng = location.lng()
    } else {
      lng = location.lng
    }

    const loc = {
      name: locationName,
      latitude: lat,
      longitude: lng,
    }

    commit("SET_SEARCH_LOCATION", loc)
  },

  async getPlacesLibrary() {
    return await GoogleMapsService.importLibrary("places")
  },

  async setAirportLocation({ commit, dispatch }, code) {
    const library = await dispatch("getPlacesLibrary")
    const request = {
      query: code,
      types: ["airport"],
    }
    const service = new library.PlacesService(document.createElement("div"))
    service.textSearch(request, (results, status) => {
      if (status === "OK") {
        console.log("setAirportLocation", results)
        commit("SET_SEARCH_LOCATION", results[0])
      }
    })
  },

  calculateAmenities({ commit, state }) {
    const amenitiesCount = {}
    state.searchResults.forEach((item) => {
      let amenities = item.description?.amenities
      if (amenities) {
        for (var i = 0; i < amenities.length; i++) {
          amenitiesCount[amenities[i]] = (amenitiesCount[amenities[i]] || 0) + 1
        }
      }
    })
    commit("SET_AMENITIES_COUNT", amenitiesCount)
    return amenitiesCount
  },

  calculateChains({ commit, dispatch, getters, state }, items = null) {
    const searchItems = items || state.searchResults
    let chains = []

    if (searchItems) {
      for (let i = 0; i < searchItems.length; i++) {
        let chain = {
          value: searchItems[i].chainCode,
          label: searchItems[i].chainName.toLowerCase().split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
        }

        // Ensure unique chains
        if (!chains.find((c) => c.value === searchItems[i].chainCode)) {
          chains.push(chain)
        }
      }

      // Sort chains alphabetically
      chains.sort((a, b) => a.label.localeCompare(b.label))
    }

    commit("SET_AVAILABLE_CHAINS", chains)

    //set state.filters.chains.highlightedOptions to preferred chains
    commit("SET_PREFERRED_CHAINS", getters.preferredChains)

    //set state.filters.chains.options to all remaining (non-preferred) chains
    commit("SET_CHAIN_OPTIONS", getters.nonPreferredChains)

    return chains
  },

  updateSortOrder({ commit }, sortOrder) {
    commit("SET_SORT_ORDER", sortOrder)
    commit("SORT_RESULTS", sortOrder)
  },

  deleteHotel({ commit }, hotelCode) {
    commit("DELETE_HOTEL", hotelCode)
  },

  saveHotel({ commit }, result) {
    commit("SET_LAST_VIEWED_HOTEL", result.hotelCode)
    commit("SAVE_HOTEL", result)
  },
}

export const getters = {
  getSearchResults: (state) => state.searchResults,
  getSavedResults: (state) => state.savedResults,
  getSearchDates: (state) => state.searchDates,
  findHotel: (state) => (hotelCode) => {
    console.log(hotelCode)
    return (
      state.savedResults.find((result) => result.hotelCode === hotelCode) ||
      null
    )
  },

  profile(state, getters, rootState) {
    return rootState.travellersAndTrips.currentTravellerProfile
  },

  tripType(state, getters, rootState) {
    return rootState.travellersAndTrips.travellers.tripType
  },

  preferredVendors(state, getters) {
    return getters.profile.preferredHotelVendors || []
  },

  getAmenities: (state) => state.amenitiesCount,

  getAvailableChains: (state) => state.availableChains,

  preferredChains: (state, getters) => {
    return state.availableChains.filter((item) =>
      getters.preferredVendors.includes(item.value)
    )
  },

  nonPreferredChains: (state, getters) => {
    return state.availableChains.filter(
      (item) => !getters.preferredVendors.includes(item.value)
    )
  },

  filteredResults(state) {
    let items = JSON.parse(JSON.stringify(state.searchResults))

    // Amenities filter
    if (state.filters.amenities.selected.length > 0) {
      items = items.filter((i) => {
        return state.filters.amenities.selected.every(
          (val) => i.description?.amenities.indexOf(val) >= 0
        )
      })
    }

    // Price Filter
    items = items.filter((i) => {
      return (
        i.cheapestPrice >= state.filters.price.range[0] &&
        i.highestPrice <= state.filters.price.range[1]
      )
    })

    // Chains Filter
    if (state.filters.chains.selected.length > 0) {
      items = items.filter((i) =>
        state.filters.chains.selected.includes(i.chainCode)
      )
    }

    // Brand Filter
    if (state.filters.brands.selected.length > 0) {
      items = items.filter((i) =>
        state.filters.brands.selected.includes(i.brandCode)
      )
    }

    // Policy Filter
    if (state.filters.policy.selected === "hard") {
      items = items.filter(
        (i) => i.travelPolicy?.summary?.rulesTriggeredCount === 0
      )
    } else if (state.filters.policy.selected === "soft") {
      items = items.filter(
        (i) => i.travelPolicy?.summary?.hardApprovalRequiredCount === 0
      )
    }

     //Preferred Hotels Filter
     if (state.filters.companyPreferred.selected === "preferred") {
      items = items.filter((i) => i.isPreferred === true)
    }

    return items
  },

  getFullAmenities: (state) => {
    return state.searchResults.reduce((amenitiesMap, item) => {
      const amenities = item.description?.amenities || []
      amenities.forEach((amenity) => {
        amenitiesMap[amenity] = (amenitiesMap[amenity] || 0) + 1
      })
      return amenitiesMap
    }, {})
  },

  getPolicies: (state, getters) => {
    const policies = {}
    getters.filteredResults.forEach((item) => {
      if (item.travelPolicy) {
        if (item.travelPolicy.summary.rulesTriggeredCount === 0) {
          policies.none = true
          policies.soft = true
        } else if (item.travelPolicy.summary.hardApprovalRequiredCount === 0) {
          policies.soft = true
        }
      }
    })
    return policies
  },
}
